/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { classifyTier } from "@cosmos/airtable-data";
import { deriveOptions, Directory, HeadingLevelContext } from "@cosmos/components";
import { scrollIntoViewEffect } from "@cosmos/components/utils";
import { CosmosEnumDirectoryVariant } from "@cosmos/hygraph-schema";
import { useMessages } from "@epic-mw/i18n";
import { useCallback, useMemo, useRef } from "react";

import { partnerBadgeMessages } from "./cards/buildPartnerCardProps";
import { InstructorCard } from "./cards/InstructorCard";
import { ServicePartnerCard } from "./cards/ServicePartnerCard";
import { TrainingPartnerCard } from "./cards/TrainingPartnerCard";
import { trainingPartnerParamPrefix } from "./paramPrefix";

import type { ServicePartnerBadgeAssets } from "./cards/buildPartnerCardProps";
import type { TrainingPartnerCardVariant } from "./types";
import type { Instructor, ServicePartner, TrainingPartner } from "@cosmos/airtable-data";
import type { DirectoryFiltersProps, FilterDef, FilterOption } from "@cosmos/components";

/**
 * Card grid layout — 3 columns at desktop, 2 at tablet (lgDown), 1 at mobile
 * (smDown). Matches the Figma layout and the canonical Cosmos card-section
 * pattern (BlogCards / FeatureCards / NewsCards).
 */
const CARD_COLUMNS = 3 as const;

const PER_PAGE = 24;

/* -------------------------------------------------------------------------- *
 * CMS chrome — narrowed per variant
 * -------------------------------------------------------------------------- */

/**
 * Grid-heading + filter-chrome fields shared by every directory variant.
 * Maps 1:1 to the Hygraph fragment scalars, sans the `directory*` prefix
 * (the prefix lives on the Hygraph side; the page-data → chrome translation
 * happens in `toCmsBodyData`).
 */
export interface DirectoryBaseChrome {
	eyebrow?: string | null;
	title?: string | null;
	/**
	 * Description rendered as HTML via `CosmosRichText`. The `Html` suffix
	 * mirrors the `DirectoryHeading.descHtml` prop and signals that the
	 * value flows through rich-text rendering, not text escaping.
	 */
	descHtml?: string | null;
	searchPlaceholder?: string | null;
	industryFilterLabel?: string | null;
	noResultsLabel?: string | null;
	/**
	 * Localizable "No results for `{0}`" template — `{0}` is replaced with the
	 * current search query at render time. Defaults to the English template
	 * when null/undefined. Forwarded through `DirectoryConfig.noResultsWithQueryLabel`.
	 */
	noResultsWithQueryTemplate?: string | null;
}

/**
 * ServicePartner-only chrome — adds the combobox label/placeholder for the
 * services filter. `serviceFilterLabel` is required so the rendered combobox
 * always has an accessible name (WCAG 4.1.2). The CMS field is nullable;
 * the page-template mapping supplies an English fallback when null.
 */
export interface DirectoryServiceChrome extends DirectoryBaseChrome {
	serviceFilterLabel: string;
	serviceFilterPlaceholder?: string | null;
	/** Partner-level (badge tier) dropdown label; falls back to the i18n message when empty. */
	badgeFilterLabel?: string | null;
}

/* -------------------------------------------------------------------------- *
 * Generic variant body — shared chrome + grid layout for all three variants
 * -------------------------------------------------------------------------- */

interface DirectoryBodyProps<T> {
	cards: T[];
	chrome: DirectoryBaseChrome;
	paramPrefix: string;
	filters: FilterDef<T>[];
	getKey: (item: T) => string;
	renderCard: (item: T) => React.ReactNode;
	/** True when the directory title is the page's top heading (no hero above it). */
	isPageTitle?: boolean;
	/** True when a hero renders above the whole page — governs how deep the grid cards nest. */
	heroPresent?: boolean;
	/** Filter row layout — see `DirectoryFiltersProps.layout`. */
	filtersLayout?: DirectoryFiltersProps["layout"];
	/** aria-label for the dropdown filters' clear button. */
	clearFilterLabel?: string;
	/** Label for the "Clear all" button next to the active-filter chips. */
	clearAllFiltersLabel?: string;
	/** When set, renders the filtered count (e.g. "12 partners") between the filters and the grid. */
	resultsCountLabel?: (count: number) => string;
}

function DirectoryBody<T>({
	cards,
	chrome,
	paramPrefix,
	filters,
	getKey,
	renderCard,
	isPageTitle,
	heroPresent,
	filtersLayout,
	clearFilterLabel,
	clearAllFiltersLabel,
	resultsCountLabel,
}: DirectoryBodyProps<T>): React.JSX.Element {
	const withQueryTemplate = chrome.noResultsWithQueryTemplate;
	const noResultsWithQueryLabel = withQueryTemplate
		? (query: string) => withQueryTemplate.replace("{0}", query)
		: undefined;
	const sectionRef = useRef<HTMLElement>(null);
	const onPageScroll = useCallback(() => {
		scrollIntoViewEffect(sectionRef.current, 0);
	}, []);
	return (
		<Directory.Section ref={sectionRef}>
			<Directory<T>
				data={cards}
				getKey={getKey}
				paramPrefix={paramPrefix}
				filters={filters}
				pagination={{ perPage: PER_PAGE }}
				noResultsLabel={chrome.noResultsLabel ?? undefined}
				noResultsWithQueryLabel={noResultsWithQueryLabel}
				clearFilterLabel={clearFilterLabel}
				clearAllFiltersLabel={clearAllFiltersLabel}
			>
				<Directory.Heading
					eyebrow={chrome.eyebrow}
					title={chrome.title}
					descHtml={chrome.descHtml}
					titleAs={isPageTitle ? "h1" : "h2"}
				/>
				<Directory.Filters layout={filtersLayout} />
				{resultsCountLabel ? <Directory.ResultsCount label={resultsCountLabel} /> : null}
				{/* Card names render h3. Nest them one level below the heading directly above the
				    grid: under the DirectoryHeading when it renders (h1→h2 as page title, else h2→h3),
				    or — with no DirectoryHeading — under the page h1 from a promoted ref-section (cards
				    h2 on a hero-less page; h3 when a hero/section stack owns the levels above). */}
				<HeadingLevelContext.Provider value={{ offset: chrome.title ? (isPageTitle ? -1 : 0) : heroPresent ? 0 : -1 }}>
					<Directory.Grid<T> columns={CARD_COLUMNS}>{renderCard}</Directory.Grid>
				</HeadingLevelContext.Provider>
				<Directory.Pagination onPageScroll={onPageScroll} />
			</Directory>
		</Directory.Section>
	);
}

/* -------------------------------------------------------------------------- *
 * Variant: GoldInstructors
 * -------------------------------------------------------------------------- */

interface InstructorsBodyProps {
	cards: Instructor[];
	chrome: DirectoryBaseChrome;
	isPageTitle?: boolean;
	heroPresent?: boolean;
}

function InstructorsDirectoryBody({
	cards,
	chrome,
	isPageTitle,
	heroPresent,
}: InstructorsBodyProps): React.JSX.Element {
	const industryOptions = useMemo(() => deriveOptions(cards, (i) => i.industries), [cards]);

	const filters = useMemo<FilterDef<Instructor>[]>(
		() => [
			{
				kind: "search",
				paramKey: "q",
				placeholder: chrome.searchPlaceholder ?? undefined,
				accessors: [
					{ type: "string", fn: (i) => i.name },
					{ type: "string", fn: (i) => i.country },
					{ type: "string", fn: (i) => i.geo },
					{ type: "array", fn: (i) => i.industries },
				],
			},
			{
				kind: "chips",
				paramKey: "industry",
				label: chrome.industryFilterLabel ?? "Industries",
				matchMode: "any",
				accessor: (i) => i.industries,
				options: industryOptions,
			},
		],
		[chrome.industryFilterLabel, chrome.searchPlaceholder, industryOptions],
	);

	return (
		<DirectoryBody<Instructor>
			cards={cards}
			chrome={chrome}
			paramPrefix="inst"
			filters={filters}
			getKey={(i) => i.id}
			renderCard={(instructor) => <InstructorCard data={instructor} />}
			isPageTitle={isPageTitle}
			heroPresent={heroPresent}
		/>
	);
}

/* -------------------------------------------------------------------------- *
 * Variants: TrainingPartners, AcademicPartners
 * -------------------------------------------------------------------------- */

interface TrainingPartnersBodyProps {
	cards: TrainingPartner[];
	chrome: DirectoryBaseChrome;
	/** URL param namespace, so each directory keeps its own filter keys. */
	paramPrefix: ReturnType<typeof trainingPartnerParamPrefix>;
	isPageTitle?: boolean;
	heroPresent?: boolean;
}

function TrainingPartnersDirectoryBody({
	cards,
	chrome,
	paramPrefix,
	isPageTitle,
	heroPresent,
}: TrainingPartnersBodyProps): React.JSX.Element {
	const industryOptions = useMemo(() => deriveOptions(cards, (p) => p.industries), [cards]);

	const filters = useMemo<FilterDef<TrainingPartner>[]>(
		() => [
			{
				kind: "search",
				paramKey: "q",
				placeholder: chrome.searchPlaceholder ?? undefined,
				accessors: [
					{ type: "string", fn: (p) => p.name },
					{ type: "string", fn: (p) => p.country },
					{ type: "array", fn: (p) => p.languages },
				],
			},
			{
				kind: "chips",
				paramKey: "industry",
				label: chrome.industryFilterLabel ?? "Industries",
				matchMode: "any",
				accessor: (p) => p.industries,
				options: industryOptions,
			},
		],
		[chrome.industryFilterLabel, chrome.searchPlaceholder, industryOptions],
	);

	return (
		<DirectoryBody<TrainingPartner>
			cards={cards}
			chrome={chrome}
			paramPrefix={paramPrefix}
			filters={filters}
			getKey={(p) => p.id}
			renderCard={(partner) => <TrainingPartnerCard data={partner} />}
			isPageTitle={isPageTitle}
			heroPresent={heroPresent}
		/>
	);
}

/* -------------------------------------------------------------------------- *
 * Variant: ServicePartners
 * -------------------------------------------------------------------------- */

/** Tiers offered in the "Partner level" filter, in display order (only those present in the data render). */
const SERVICE_PARTNER_TIERS = ["gold", "silver", "bronze"] as const;

interface ServicePartnersBodyProps {
	cards: ServicePartner[];
	chrome: DirectoryServiceChrome;
	badgeAssets?: ServicePartnerBadgeAssets;
	isPageTitle?: boolean;
	heroPresent?: boolean;
}

function ServicePartnersDirectoryBody({
	cards,
	chrome,
	badgeAssets,
	isPageTitle,
	heroPresent,
}: ServicePartnersBodyProps): React.JSX.Element {
	const messages = useMessages();
	const industryOptions = useMemo(() => deriveOptions(cards, (p) => p.industries), [cards]);
	const serviceOptions = useMemo(() => deriveOptions(cards, (p) => p.supportType), [cards]);
	const tierOptions = useMemo(() => {
		const badgeLabels = partnerBadgeMessages(messages);
		const present = new Set(cards.map((p) => classifyTier(p.badgeLevel)));
		return SERVICE_PARTNER_TIERS.filter((tier) => present.has(tier)).map<FilterOption>((tier) => ({
			value: tier,
			label: badgeLabels[tier],
		}));
	}, [cards, messages]);

	const industryLabel = chrome.industryFilterLabel ?? "Industries";
	const partnerLevelLabel =
		chrome.badgeFilterLabel || messages["epic.cosmos.directory.filter.partnerLevel"] || "Partner level";

	// Figma 693:2205 / 698:14246 (MW-14363): search on the left, three
	// multi-select dropdowns on the right showing "Label (N)"; selections
	// render as removable chips below. Every filter persists in the URL (`sp_q`,
	// `sp_industry`, `sp_service`, `sp_tier`) so filtered views are shareable.
	const filters = useMemo<FilterDef<ServicePartner>[]>(
		() => [
			{
				kind: "search",
				paramKey: "q",
				placeholder: chrome.searchPlaceholder ?? undefined,
				persistInUrl: true,
				accessors: [
					{ type: "string", fn: (p) => p.name },
					{ type: "array", fn: (p) => p.countriesSupported },
					{ type: "array", fn: (p) => p.officeLocations },
					{ type: "array", fn: (p) => p.languages },
				],
			},
			{
				kind: "combobox",
				paramKey: "industry",
				ariaLabel: industryLabel,
				placeholder: industryLabel,
				accessor: (p) => p.industries,
				options: industryOptions,
				label: "",
				matchMode: "any",
			},
			{
				// PRD "Services Offered" → Airtable `Support Type`.
				kind: "combobox",
				paramKey: "service",
				ariaLabel: chrome.serviceFilterLabel,
				placeholder: chrome.serviceFilterPlaceholder || chrome.serviceFilterLabel,
				accessor: (p) => p.supportType,
				options: serviceOptions,
				label: "",
				matchMode: "any",
			},
			{
				kind: "combobox",
				paramKey: "tier",
				ariaLabel: partnerLevelLabel,
				placeholder: partnerLevelLabel,
				accessor: (p) => [classifyTier(p.badgeLevel)],
				options: tierOptions,
				label: "",
				matchMode: "any",
			},
		],
		[
			chrome.searchPlaceholder,
			chrome.serviceFilterLabel,
			chrome.serviceFilterPlaceholder,
			industryLabel,
			industryOptions,
			partnerLevelLabel,
			serviceOptions,
			tierOptions,
		],
	);

	return (
		<DirectoryBody<ServicePartner>
			cards={cards}
			chrome={chrome}
			paramPrefix="sp"
			filters={filters}
			getKey={(p) => p.id}
			renderCard={(partner) => <ServicePartnerCard data={partner} badgeAssets={badgeAssets} />}
			isPageTitle={isPageTitle}
			heroPresent={heroPresent}
			filtersLayout="searchStart"
			clearFilterLabel={messages["epic.cosmos.directory.filter.clear"] ?? "Clear filter"}
			clearAllFiltersLabel={messages["epic.cosmos.directory.filter.clearAll"] ?? "Clear all"}
			resultsCountLabel={(count) =>
				(count === 1
					? (messages["epic.cosmos.directory.partnerCount.one"] ?? "{0} partner")
					: (messages["epic.cosmos.directory.partnerCount"] ?? "{0} partners")
				).replace("{0}", String(count))
			}
		/>
	);
}

/* -------------------------------------------------------------------------- *
 * Dispatcher
 * -------------------------------------------------------------------------- */

/**
 * Discriminated input for the body — the page template builds this from the
 * Hygraph fragment + Airtable fetch.
 *
 * Service-specific chrome (`serviceFilterLabel`/`serviceFilterPlaceholder`)
 * is narrowed onto the `ServicePartners` branch only — other variants can't
 * be passed unused service filter fields.
 */
export type DirectoryCmsBodyData =
	| { variant: typeof CosmosEnumDirectoryVariant.GoldInstructors; cards: Instructor[]; chrome: DirectoryBaseChrome }
	| {
			variant: TrainingPartnerCardVariant;
			cards: TrainingPartner[];
			chrome: DirectoryBaseChrome;
	  }
	| {
			variant: typeof CosmosEnumDirectoryVariant.ServicePartners;
			cards: ServicePartner[];
			chrome: DirectoryServiceChrome;
			badgeAssets?: ServicePartnerBadgeAssets;
	  };

export interface DirectoryCmsBodyProps {
	data: DirectoryCmsBodyData;
	/** True when a hero or top ref-sections already own the page's top heading above the
	 * directory — then its title stays h2, not h1 (avoids a second h1). */
	hasHero?: boolean;
	/** True only when an actual hero renders above the page — controls how deep the grid cards
	 * nest (independent of topRefSections, which sit inline above the grid, not as a hero). */
	heroPresent?: boolean;
}

/**
 * Dispatches to the variant-specific Directory body. Each variant configures
 * its own filter set and card adapter; chrome is narrowed per variant via
 * the discriminated `data` union.
 *
 * Filter chrome strings come from the CMS via the `chrome` field; English
 * fallbacks live in `@epic-mw/i18n` and are read by the cards themselves.
 */
export function DirectoryCmsBody({ data, hasHero, heroPresent }: DirectoryCmsBodyProps): React.JSX.Element {
	// The directory title is the page's top heading when no hero renders above it.
	const isPageTitle = !hasHero;
	switch (data.variant) {
		case CosmosEnumDirectoryVariant.GoldInstructors:
			return (
				<InstructorsDirectoryBody
					cards={data.cards}
					chrome={data.chrome}
					isPageTitle={isPageTitle}
					heroPresent={heroPresent}
				/>
			);
		case CosmosEnumDirectoryVariant.TrainingPartners:
		case CosmosEnumDirectoryVariant.AcademicPartners:
			return (
				<TrainingPartnersDirectoryBody
					cards={data.cards}
					chrome={data.chrome}
					paramPrefix={trainingPartnerParamPrefix(data.variant)}
					isPageTitle={isPageTitle}
					heroPresent={heroPresent}
				/>
			);
		case CosmosEnumDirectoryVariant.ServicePartners:
			return (
				<ServicePartnersDirectoryBody
					cards={data.cards}
					chrome={data.chrome}
					badgeAssets={data.badgeAssets}
					isPageTitle={isPageTitle}
					heroPresent={heroPresent}
				/>
			);
		default: {
			// Exhaustive check — TS catches new variants at compile time;
			// runtime throws (matches every other variant dispatcher in
			// cosmos-page-directory) so a typed bypass fails consistently.
			const exhaustiveCheck: never = data;
			throw new Error(
				`[DirectoryCmsBody] Unhandled variant: ${String((exhaustiveCheck as { variant?: string }).variant)}`,
			);
		}
	}
}
